import { CUSTOM_EMOJI_RE, encodeMentions, encodeRoleMentions } from '@clanfire/shared';
import { emojiByName } from '@/lib/emoji';
import { useServers } from '@/stores/servers';
import { useUsers } from '@/stores/users';

/**
 * What you type in the message box (`@username`, `@Role Name`, `:emoji_name:`)
 * becomes tokens (`<@id>`, `<@&id>`, `<:name:id>`) when sent, so they survive
 * renames. Editing a message turns them back into what you'd type.
 */

/** A server's custom emoji first, then those of your other servers. */
function customEmojiByName(serverId: string | null | undefined): Map<string, { id: string; name: string }> {
  const all = Object.values(useServers.getState().emojis);
  const byName = new Map<string, { id: string; name: string }>();
  for (const e of all) if (e.serverId !== serverId && !byName.has(e.name.toLowerCase())) byName.set(e.name.toLowerCase(), e);
  for (const e of all) if (e.serverId === serverId) byName.set(e.name.toLowerCase(), e);
  return byName;
}

export function encodeComposerText(text: string, opts: { serverId?: string | null; usernames: Map<string, string> }): string {
  let content = text;
  if (opts.serverId) {
    // Role names can contain spaces, so roles go first; a role named exactly like a member is left to the member.
    // "@everyone" is never a role or a member.
    const roles = Object.values(useServers.getState().roles).filter(
      (r) => r.serverId === opts.serverId && !r.isDefault && !opts.usernames.has(r.name.toLowerCase()) && r.name.toLowerCase() !== 'everyone',
    );
    content = encodeRoleMentions(content, roles);
  }
  content = encodeMentions(content, (u) => (u === 'everyone' ? undefined : opts.usernames.get(u)));
  // :name: for a custom emoji (built-in emoji win if the names clash).
  const custom = customEmojiByName(opts.serverId);
  if (custom.size) {
    content = content.replace(/(?<!<a?):([A-Za-z0-9_]{2,32}):(?![a-z0-9]{8,40}>)/g, (full, name: string) => {
      if (emojiByName(name.toLowerCase())) return full;
      const emoji = custom.get(name.toLowerCase());
      return emoji ? `<:${emoji.name}:${emoji.id}>` : full;
    });
  }
  return content;
}

/** Message content as you'd type it, for the edit box. */
export function decodeForEditing(content: string): string {
  const users = useUsers.getState().users;
  const { roles, emojis } = useServers.getState();
  return (
    content
      .replace(/<@([a-z0-9]{8,40})>/g, (m, id: string) => (users[id] ? `@${users[id]!.username}` : m))
      .replace(/<@&([a-z0-9]{8,40})>/g, (m, id: string) => (roles[id] ? `@${roles[id]!.name}` : m))
      // Only emoji you have (others couldn't be turned back into the same emoji).
      .replace(CUSTOM_EMOJI_RE, (m, name: string, id: string) => (emojis[id] ? `:${name}:` : m))
  );
}
